import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { monitoringAPI } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { 
  Video, 
  Search, 
  Play,
  Clock,
  Monitor,
  RefreshCw,
  User,
  MapPin,
  Calendar,
  Eye,
  Shield,
  AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';

const LoginRecordings = () => {
  const { user } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecording, setSelectedRecording] = useState(null);
  const [showDialog, setShowDialog] = useState(false);

  useEffect(() => {
    fetchRecordings();
    // Auto-refresh every 30 seconds
    const interval = setInterval(fetchRecordings, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchRecordings = async () => {
    try {
      setLoading(recordings.length === 0);
      setRefreshing(recordings.length > 0);
      const response = await monitoringAPI.getScreenRecordings();
      setRecordings(response.data);
    } catch (error) {
      console.error('Recordings fetch error:', error);
      if (recordings.length === 0) {
        toast.error('Failed to load recordings');
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const openRecordingDialog = (recording) => {
    setSelectedRecording(recording);
    setShowDialog(true);
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const then = new Date(timestamp);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    return `${diffDays} days ago`;
  };

  const filteredRecordings = recordings.filter(rec =>
    rec.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    rec.employee_id?.includes(searchQuery)
  );

  // Get recent recordings for quick stats
  const todayCount = recordings.filter(r => {
    const recDate = new Date(r.timestamp);
    const today = new Date();
    return recDate.toDateString() === today.toDateString();
  }).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4" />
          <p className="text-slate-500">Loading recordings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="login-recordings-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-violet-600 to-purple-600 text-white border-none">
        <CardContent className="p-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/10 rounded-2xl flex items-center justify-center">
                <Video className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Login Screen Recordings
                </h2>
                <p className="text-violet-100">
                  5-second recordings captured at employee login
                </p>
              </div>
            </div>
            <Button 
              onClick={fetchRecordings}
              disabled={refreshing}
              className="bg-white/10 hover:bg-white/20 text-white border-white/20"
              data-testid="refresh-recordings-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Total Recordings</p>
                <p className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {recordings.length}
                </p>
              </div>
              <Video className="w-10 h-10 text-blue-200" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Today's Logins</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {todayCount}
                </p>
              </div>
              <Calendar className="w-10 h-10 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Recording Duration</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  5s
                </p>
              </div>
              <Clock className="w-10 h-10 text-violet-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 text-sm">Unique Employees</p>
                <p className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {[...new Set(recordings.map(r => r.employee_id))].length}
                </p>
              </div>
              <User className="w-10 h-10 text-indigo-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Info Banner */}
      <Card className="bg-indigo-50 border-indigo-200">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-indigo-600" />
            <div>
              <p className="font-medium text-indigo-800">Security Feature</p>
              <p className="text-sm text-indigo-600">
                5-second screen recordings are automatically captured when employees log in. 
                This helps detect unauthorized access and monitor security compliance.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          placeholder="Search by employee name or ID..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-white"
          data-testid="recordings-search"
        />
      </div>

      {/* Recordings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredRecordings.map((recording) => (
          <Card 
            key={recording.id} 
            className="card-hover overflow-hidden"
            data-testid={`recording-card-${recording.id}`}
          >
            <div className="relative">
              {/* Video Thumbnail */}
              <div className="aspect-video bg-slate-900 relative overflow-hidden">
                <img 
                  src={recording.thumbnail_url || `https://picsum.photos/seed/${recording.employee_id}/320/180`}
                  alt="Recording thumbnail"
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                
                {/* Play Button Overlay */}
                <button
                  onClick={() => openRecordingDialog(recording)}
                  className="absolute inset-0 flex items-center justify-center group"
                  data-testid={`play-btn-${recording.id}`}
                >
                  <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg">
                    <Play className="w-6 h-6 text-violet-600 ml-1" />
                  </div>
                </button>

                {/* Duration Badge */}
                <Badge className="absolute top-2 right-2 bg-black/60 text-white">
                  <Clock className="w-3 h-3 mr-1" />
                  {recording.duration || 5}s
                </Badge>

                {/* Activity Type */}
                <Badge className="absolute top-2 left-2 bg-violet-600 text-white">
                  {recording.activity_type === 'login' ? 'Login' : 'Activity'}
                </Badge>
              </div>
            </div>

            <CardContent className="p-4">
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="w-10 h-10">
                  <AvatarImage src={`https://ui-avatars.com/api/?name=${recording.employee_name}&background=6366f1&color=fff`} />
                  <AvatarFallback className="bg-violet-100 text-violet-600 text-xs">
                    {recording.employee_name?.split(' ').map(n => n[0]).join('') || '?'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">
                    {recording.employee_name || 'Unknown Employee'}
                  </p>
                  <p className="text-xs text-slate-500 truncate">
                    {recording.position || 'Position not available'}
                  </p>
                </div>
              </div>

              <div className="space-y-1 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <Calendar className="w-3 h-3" />
                  <span>{formatTimestamp(recording.timestamp)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-3 h-3" />
                  <span>{getTimeAgo(recording.timestamp)}</span>
                </div>
              </div>

              <Button 
                variant="outline" 
                size="sm"
                className="w-full mt-3"
                onClick={() => openRecordingDialog(recording)}
              >
                <Eye className="w-4 h-4 mr-2" />
                View Recording
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredRecordings.length === 0 && (
        <div className="text-center py-12">
          <Video className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-slate-900 mb-2">No Recordings Found</h3>
          <p className="text-slate-500">
            {recordings.length === 0 
              ? 'Recordings will appear here after employees log in' 
              : 'Try adjusting your search'}
          </p>
        </div>
      )}

      {/* Recording Player Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
              Login Recording - {selectedRecording?.employee_name}
            </DialogTitle>
            <DialogDescription>
              5-second screen capture at login
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Video Player */}
            <div className="aspect-video bg-slate-900 rounded-lg overflow-hidden relative">
              <img 
                src={selectedRecording?.thumbnail_url || `https://picsum.photos/seed/${selectedRecording?.employee_id}/640/360`}
                alt="Recording"
                className="w-full h-full object-cover"
              />
              
              {/* Simulated Video Player Controls */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
                <div className="flex items-center gap-3">
                  <Button size="sm" className="bg-white text-slate-900 hover:bg-slate-100">
                    <Play className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 bg-white/30 rounded-full h-1.5 overflow-hidden">
                    <div className="bg-white h-full w-2/3 rounded-full" />
                  </div>
                  <span className="text-white text-sm font-mono">0:03 / 0:05</span>
                </div>
              </div>

              {/* Simulated Playback Indicator */}
              <div className="absolute top-4 left-4 px-3 py-1 bg-red-600 rounded-full flex items-center gap-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
                <span className="text-white text-xs font-medium">REC</span>
              </div>
            </div>

            {/* Recording Details */}
            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Avatar className="w-12 h-12">
                      <AvatarImage src={`https://ui-avatars.com/api/?name=${selectedRecording?.employee_name}&background=6366f1&color=fff`} />
                      <AvatarFallback className="bg-violet-100 text-violet-600">
                        {selectedRecording?.employee_name?.split(' ').map(n => n[0]).join('')}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-semibold text-slate-900">{selectedRecording?.employee_name}</p>
                      <p className="text-sm text-slate-500">{selectedRecording?.position}</p>
                      <p className="text-xs text-slate-400">ID: {selectedRecording?.employee_id}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Timestamp</span>
                    <span className="font-medium text-slate-900">
                      {selectedRecording && formatTimestamp(selectedRecording.timestamp)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Duration</span>
                    <span className="font-medium text-slate-900">{selectedRecording?.duration || 5} seconds</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500">Activity</span>
                    <Badge className="bg-violet-100 text-violet-700">
                      {selectedRecording?.activity_type === 'login' ? 'Login Event' : 'Activity'}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Alert for Demo */}
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-3">
                <div className="flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-4 h-4" />
                  <p className="text-sm">
                    <strong>Demo Mode:</strong> This is a simulated recording preview. 
                    In production, actual 5-second screen captures would be recorded via a desktop agent.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LoginRecordings;
